"use client";

import { IconCheck, IconCopy } from "@decibeltrade/react-icons";
import { ButtonProps, IconButton } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { cva, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { Clickable } from "../clickable";
import { openCopyWarningModal } from "../modal/copy-warning-modal";

type BaseCopyButtonProps = Omit<ButtonProps, "children" | "variant"> & {
  /** The value to copy to the clipboard. */
  value: string;
  /** The delay in milliseconds to reset the copied state. */
  resetDelayMs?: number;
  /** The children to render inside the button. */
  children?: React.ReactNode;
  /** Optional warning message to display before copying. */
  warningMessage?: string;
  /** Fired once the value is actually on the clipboard, not merely on click. */
  onCopy?: () => void;
};

type CopyButtonPropsWithoutVariant = BaseCopyButtonProps & {
  variant?: never;
  /** Whether to only copy on icon click or also on the button click. Only available when variant is not defined. */
  onlyIconClick?: boolean;
};

type CopyButtonPropsWithVariant = BaseCopyButtonProps & {
  variant: NonNullable<ButtonProps["variant"]>;
  onlyIconClick?: never;
};

type CopyButtonProps = CopyButtonPropsWithoutVariant | CopyButtonPropsWithVariant;

export function CopyButton({
  value,
  className,
  size,
  resetDelayMs = 3_000,
  variant,
  onlyIconClick,
  children,
  ariaLabel,
  warningMessage,
  onCopy,
  ...rest
}: CopyButtonProps) {
  const t = useTranslations("general");

  const [copied, setCopied] = useState(false);

  const handleCopy = async (
    e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();

    if (copied) return;

    if (warningMessage) {
      openCopyWarningModal({
        warningMessage,
        valueToCopy: value,
        onConfirm: () => {
          setCopied(true);
          setTimeout(() => setCopied(false), resetDelayMs);
          onCopy?.();
        },
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), resetDelayMs);
      onCopy?.();
    } catch {
      // Intentionally no-op; avoid noisy UI on permission errors
    }
  };

  // No padding or background applied - just the icon
  if (!variant) {
    if (onlyIconClick) {
      return (
        <div
          className={cx(
            flex({
              gap: "8",
              alignItems: "center",
            }),
            className,
          )}
        >
          {children}
          <Clickable
            onClick={handleCopy}
            ariaLabel={ariaLabel || t(copied ? "copied" : "copy")}
            className={cx(
              flex({
                transition: "all",
                transitionDuration: "100",
                color: "text.primary",
                _hover: { color: "text.secondary" },
              }),
            )}
            {...rest}
          >
            {copied ? (
              <IconCheck className={iconSizeStyles({ size })} />
            ) : (
              <IconCopy className={iconSizeStyles({ size })} />
            )}
          </Clickable>
        </div>
      );
    }

    return (
      <Clickable
        onClick={handleCopy}
        ariaLabel={ariaLabel || t(copied ? "copied" : "copy")}
        className={cx(
          flex({
            gap: "8",
            transition: "all",
            transitionDuration: "100",
            color: "text.primary",
            _hover: { color: "text.secondary" },
          }),
          className,
        )}
        {...rest}
      >
        {children}
        {copied ? (
          <IconCheck className={iconSizeStyles({ size })} />
        ) : (
          <IconCopy className={iconSizeStyles({ size })} />
        )}
      </Clickable>
    );
  }

  return (
    <IconButton
      onClick={handleCopy}
      ariaLabel={ariaLabel || t(copied ? "copied" : "copy")}
      className={cx(className)}
      size={size}
      variant={variant}
      {...rest}
    >
      {children}
      {copied ? <IconCheck /> : <IconCopy />}
    </IconButton>
  );
}

const iconSizeStyles = cva({
  variants: {
    size: {
      sm: {
        width: "12",
        height: "12",
      },
      md: {
        width: "16",
        height: "16",
      },
      lg: {
        width: "20",
        height: "20",
      },
    },
  },
  defaultVariants: {
    size: "md",
  },
});
